import { useEffect, useRef } from 'react';

/**
 * ¿Hay que tirar la sesión y mandar al login?
 *
 * Es la misma condición que vivía suelta en el cuerpo de `DashboardLayout`,
 * extraída a función pura para poder probarla y para que el componente no la
 * evalúe con efectos colaterales colgando.
 *
 * Las tres causas, tal cual estaban:
 *  1. La carga de datos de login falló (`loadingDataError`).
 *  2. No hay usuario, ya no se está validando el token, y hubo error de login.
 *  3. La ruta exige sesión (`validateAuth`) y no hay token en localStorage.
 *
 * @param {object} params
 * @param {object} params.user - Slice `user` de Redux.
 * @param {boolean} params.validateAuth - Si la ruta exige sesión.
 * @param {boolean} params.hasToken - Si hay token en localStorage.
 * @returns {boolean}
 */
export const shouldClearSession = ({ user, validateAuth, hasToken }) =>
  Boolean(user?.loadingDataError)
  // El original hacía `!user?.data.id`, que revienta si `data` no existe.
  // Con `?.` el resultado es idéntico cuando `data` existe, y deja de tirar
  // un TypeError cuando no.
  || Boolean(!user?.data?.id && !user?.validatingToken && user?.loginError)
  || Boolean(validateAuth && !hasToken);

/**
 * Ejecuta el cierre de sesión UNA sola vez, en un efecto y no durante el render.
 *
 * ## Por qué existe (DASHBOARD-ZC-HQ, "Too many re-renders")
 *
 * `DashboardLayout` llamaba a `clearData()` **en el cuerpo del componente**,
 * o sea durante el render. Y `clearData()` hace tres cosas con efecto:
 * `history.replace('/login')`, `localStorage.clear()` y
 * `dispatch(clearDataAction())`.
 *
 * Ese `dispatch` en fase de render provoca una actualización de estado mientras
 * el componente se está renderizando: React vuelve a renderizar en el acto.
 * Y la condición **no se apagaba sola** — al contrario, `localStorage.clear()`
 * borra el token, así que la tercera causa (`validateAuth && !hasToken`) pasa a
 * ser verdadera **para siempre**. Render → clearData → dispatch → render →
 * clearData → … hasta que React corta con *"Too many re-renders"*.
 *
 * Encaja con el reporte de Sentry: el frame de primera mano es el `dispatch` de
 * `LOAD_LOGIN_DATA_FINISHED` en `userDuck` — el despacho asíncrono que dispara
 * el render donde el bucle arranca — y el componentStack empieza en
 * `DashboardLayout`.
 *
 * El arreglo es sacar el efecto del render. Con `shouldClear` como única
 * dependencia, el cierre corre **al pasar la condición a verdadera** y no se
 * repite mientras siga verdadera — que es justo lo que hacía falta, porque la
 * condición es auto-sostenida (`localStorage.clear()` la mantiene viva). Si la
 * sesión se recupera y vuelve a caerse, se dispara otra vez.
 *
 * `onClear` se lee desde un ref a propósito: así **no** entra en las
 * dependencias del efecto. Un callback nuevo no re-dispara el cierre, y aun así
 * siempre se ejecuta la versión más reciente.
 *
 * @param {object} params
 * @param {boolean} params.shouldClear
 * @param {() => void} params.onClear
 */
export function useSessionGuard({ shouldClear, onClear }) {
  const onClearRef = useRef(onClear);
  onClearRef.current = onClear;

  useEffect(() => {
    if (!shouldClear) return;
    onClearRef.current();
  }, [shouldClear]);
}

export default useSessionGuard;
